import { TransactionType } from "./enums/TransactionType";
import { IssuedBy } from "./const/Transactions";
import { FileUploadConfig, UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { IncreaseType } from "@/shareholderRegister/transactions/issue/issueTransactionDomain";
import { SettlementMethods } from "@/shareholderRegister/transactions/groupContribution/groupContributionDomain";
import { DistributionMode } from "@/shareholderRegister/transactions/dividend/dividendDomain";
import { Purpose } from "@/shareholderRegister/transactions/commonCapitalReduction";

import { SharenumberInterval } from "@/shareholderRegister/shareholderRegisterDomain";
import { Amount, LocalizedText } from "@/common/common";
import { ShareTypeCode } from "@/common/share";
import type { CompanyStatus } from "./CompanyInfo";

export interface TransactionDetails {
  buyerBrokerFee: number;
  buyerOtherCosts: number;
  currency: string;
  numberOfShares: number;
  numberOfSharesAfter?: number;
  numberOfSharesBefore?: number;
  shareNominalValueBefore?: number;
  shareNominalValueAfter?: number;
  pricePerShare: number;
  sellerBrokerFee: number;
  sellerOtherCosts: number;
  shareTypeId: number;
  shareTypeCode?: ShareTypeCode;
  shareholderBuyerId?: number;
  shareholderBuyerName?: string;
  shareholderSellerId?: number;
  shareholderSellerName?: string;
  totalPrice: number;
  transactionType: TransactionType;
  dividendPaidAmount?: number;
  sharePremiumChange?: number;
  totalAmountPaid?: number;
  averageSharePremiumPerShare?: number;
  shareCapitalReductionAmount?: number;
  sharePremiumReductionAmount?: number;
}

export interface Transaction {
  comment?: string;
  numberOfSharesBefore: NumberOfSharesPerShareType[];
  descriptions: LocalizedText[];
  organizationnumber: string;
  transactionDate: string;
  transactionDetails: TransactionDetails[];
  transactionlogId: number;
  updatedDate: string;
  transactionType: TransactionType;
  userName: string;
  fileUploadConfig?: FileUploadConfig;
  attachments: UploadedAttachment[];
  payload?: unknown;
  projectId?: string;
  cancellationAmounts?: CancellationShareTypeAmounts[];
}

export interface CancellationShareTypeAmounts {
  shareTypeId: number;
  averageSharePremiumPerShare?: number;
  shareCapitalReduction?: number;
  sharePremiumReduction?: number;
}

export interface NumberOfSharesPerShareType {
  shareTypeId: number;
  numberOfShares: number;
}

export interface BuyDetails {
  buyerShareholderId: number;
  shareTypeId: number;
  numberOfShares: number;
  sellingPrice: number | null;
  currency: string;
  buyerBrokerFee?: number;
  buyerOtherCosts?: number;
  sellerBrokerFee?: number;
  sellerOtherCosts?: number;
  taxContinuity: boolean;
  sellerShareholderId?: number;
}

export interface NewTransaction {
  organizationnumber: string;
  sellerShareholderId?: number;
  saleDetails: BuyDetails[];
  comment?: string;
  transactionDate: string;
  transactionType:
    | TransactionType.BUY_SELL
    | TransactionType.GIFT
    | TransactionType.INHERITANCE
    | TransactionType.OTHER_TRANSFER;
}

export interface TransactionAllShares {
  organizationnumber: string;
  transactionDate: string;
  buyerShareholderId: number;
  brokerFee?: number;
  otherCosts?: number;
  sellerOtherCosts?: number;
  comment?: string;
  includeCompanyOwnShares: boolean;
  sharetypeSellingPrices: Array<{
    sellingPrice: number;
    currency: string;
    sharetypeId: number;
    sellerBrokerFee?: number;
    buyerBrokerFee?: number;
  }>;
}

export interface ShareSplitTransaction {
  organizationnumber: string;
  splitRatio: number;
  decisionDate: string;
}

export interface ShareTypeAdjustment {
  sharetypeId: number;
  currentNumberOfShares: number;
  adjustedToNumberOfShares: number;
}

export interface FractionShareAdjustment {
  shareholderId: number;
  sharetypesAdjustment?: Array<ShareTypeAdjustment>;
}

export interface ShareReverseSplit {
  organizationnumber: string;
  reverseSplitRatio: number;
  fractionShareAdjustments: Array<FractionShareAdjustment>;
  decisionDate: string;
}

export interface SharetypeCancelSpecification {
  shareTypeId: number;
  numberOfShares: number;
  shareCapitalReductionAmount?: number;
  sharePremiumReductionAmount?: number;
  averageSharePremiumPerShare?: number;
}

export interface ShareholderSharetypeSpecification {
  shareTypeId: number | "";
  numberOfShares: number | "";
  totalAmount?: number;
  dividendAmount?: number;
}

export interface ShareholderCancellationSpecification {
  shareholderId: number | "";
  shareholderSharetypeSpecifications?: Array<ShareholderSharetypeSpecification>;
}

export interface CancellationOfShares {
  organizationnumber: string;
  shareholderSharetypeSpecifications: Array<SharetypeCancelSpecification>;
  shareReductionPurpose: Purpose | "";
  shareholderCancellationSpecifications?: Array<ShareholderCancellationSpecification>;
  resolutionDate?: string;
  isRegisteredInNorwegianBusinessEnterprise: boolean;
  registrationInNorwegianRegisterDate?: string | null;
  isCancellationProRata: boolean;
  isAutoCalculate: boolean;
}

export interface ReductionOfNominalValue {
  organizationnumber: string;
  shareReductionPurpose: Purpose | "";
  isTreatedAsRepayment: boolean;
  reduceInNominalValuePerShare: string;
  amountToBePaidInExcessOfReductionShareCapital: Record<number, Amount> | null;
  resolutionDate?: string;
  isRegisteredInNorwegianBusinessEnterprise: boolean;
  registrationInNorwegianRegisterDate?: string | null;
}

export interface PerSharetypePayment {
  sharetypeId: number;
  totalAmountForShareType: Amount;
}

export interface PayDividendRecipients {
  shareholderId: number;
  perSharetypePayment: PerSharetypePayment[];
}

export interface DividendWithholdingTaxAmount {
  sharetypeId: number;
  amount: Amount;
}

export interface DividendWithholdingTax {
  shareholderId: number;
  percentage: number;
  countryCode: string;
  amounts: DividendWithholdingTaxAmount[];
}

export enum DividendPaymentMethod {
  PER_SHARE = "perShare",
  TOTAL_AMOUNT = "totalAmount",
}

export interface DividendPayment {
  sharetypeId: number;
  paymentMethod: DividendPaymentMethod;
  amount: Amount;
  excludeCompanysOwnShares: boolean;
}
export interface PayDividendPayment {
  dividendPayment: DividendPayment[];
  evenDistributionRecipientsAtDecisionTime: boolean;
  evenDistributionRecipientsAtSpecificTime?: string | null;
}

export interface PayDividend {
  organizationnumber: string;
  resolutionBy: Resolution | "";
  decisionDate: string;
  dividendDistributionMode: DistributionMode | "";
  evenDividendPayment?: PayDividendPayment;
  perShareholderDividendPayment?: PayDividendRecipients[];
  resolutionIsBasedOnIntermediateBalanceSheet: boolean;
  repaimentOfPaidInCapital: boolean;
  withholdingTaxPerShareholder?: DividendWithholdingTax[];
  repaymentCalculation?: RepaymentCalculation;
  manualSplit?: DividendSplit[];
}

export enum RepaymentCalculation {
  AUTO = "AUTO",
  MANUAL = "MANUAL",
}

export interface DividendSplit {
  shareholderId: number;
  sharetypeId: number;
  dividendAmount: Amount;
  sharePremiumRepaymentAmount: Amount;
  shareCapitalRepaymentAmount: Amount;
}

export interface GroupContributionCompany {
  organizationnumber: string;
  companyName: string;
  settlementMethod: SettlementMethods | "";
  amount: Amount;
}

export interface RegisterGroupContribution {
  organizationnumber: string;
  resolutionBy: Resolution | "";
  isResolutionBasedOnIntermediateBalanceSheet: boolean;
  hasBalanceBeenAudited?: boolean;
  balanceSheetDate?: string;
  decisionDate: string;
  groupContributionCompanies: GroupContributionCompany[];
}

export enum Resolution {
  POA = "PoA",
  GENERAL_MEETING = "generalMeeting",
}

export enum PaymentMethods {
  PER_SHARE = "perShare",
  TOTAL_INCL = "totalAmountIncl",
  TOTAL_EX = "totalAmountEx",
}

export enum GroupMethod {
  ESTABLISH_CLAIM = "ESTABLISH_CLAIM",
  SET_OFF_OTHER_BALANCE = "SET_OFF_OTHER_BALANCE",
  CASH_PAYMENT = "CASH_PAYMENT",
  OTHER_METHOD = "OTHER_METHOD",
}

export interface SharePriceChangeDTO {
  shareId: number;
  previousValue: number;
  newValue: number;
}

export interface IssueShareTypeDTO {
  sharetypeId: number;
  numberOfShares: number;
  shareholderId: number;
  sharePrice: number;
  currency: string;
  sharePremium?: number;
  paidInShareCapital?: number;
  bonusShareCapital?: number;
  isEmployeeShare?: boolean;
}

export interface AmountPaidInPerShareTypeDTO {
  sharetypeId: number;
  sharePrice: number;
}

export type IssueNewSharesDTO = {
  organizationnumber: string;
  registrationDate?: string;
  shareholderRightsDate?: string;
  shareAllocation: IssueShareTypeDTO[];
  isBonusIssue?: boolean;
  transactionType: TransactionType.ISSUE;
  issuedBy?: IssuedBy;
  boardMeetingPoDDate?: string | null;
  generalMeetingDate?: string | null;
  registeredInNorwegian?: boolean;
  incorporationDate?: string;
  increaseType?: IncreaseType;
  shareNominalValue?: number;
  isPending?: boolean;
  demergerData?: DemergerData;
  optionExerciseIds?: string[];
};

export type DemergerData = {
  demergerType: DemergerType;
  transferorCompany: NamedOrganisationNumber;
  transferorNominalValue: number;
  demergerShareTypeData: DemergerShareTypeData[];
};

export type DemergerShareTypeData = {
  shareTypeId: number;
  transferorShareTypeCode: ShareTypeCode;
  transferorNumberOfShares: number;
  transferorShareCapital: number;
  transferorSharePremium: number;
};

export type IncreaseNominalValueDTO = {
  organizationnumber: string;
  registrationDate?: string;
  isBonusIssue: boolean;
  issuedBy?: IssuedBy;
  boardMeetingPoDDate?: string | null;
  generalMeetingDate?: string | null;
  amountPaidInPerShare: AmountPaidInPerShareTypeDTO[];
  increaseInNominalValuePerShare: number;
  increaseType?: IncreaseType;
  isPending?: boolean;
};

export type ShareSplitDTO = {
  organizationnumber: string;
  splitRatio: number;
  comment?: string | null;
};

export type ShareReverseSplitDTO = {
  organizationnumber: string;
  reverseSplitRatio: number;
  fractionShareAdjustments: FractionShareAdjustment[];
};

export type ReductionOfNominalValueDTO = {
  organizationnumber: string;
  shareReductionPurpose: Purpose;
  reduceInNominalValuePerShare: string;
  amountToBePaidInExcessOfReductionShareCapital?: Record<string, Amount> | null;
  isTreatedAsRepayment?: boolean;
  resolutionDate?: string | null;
  isRegisteredInNorwegianBusinessEnterprise: boolean;
  registrationInNorwegianRegisterDate?: string | null;
};

export type CancellationOfSharesDTO = {
  organizationnumber: string;
  shareholderSharetypeSpecifications: {
    shareTypeId: number;
    numberOfShares: number;
    shareCapitalReductionAmount?: number;
    sharePremiumReductionAmount?: number;
    averageSharePremiumPerShare?: number;
  }[];
  shareReductionPurpose: Purpose;
  shareholderCancellationSpecifications: {
    shareholderId: number;
    shareholderSharetypeSpecifications: {
      shareTypeId: number;
      numberOfShares: number;
      totalAmount?: number;
      dividendAmount?: number;
    }[];
  }[];
  resolutionDate: string;
  isRegisteredInNorwegianBusinessEnterprise: boolean;
  registrationInNorwegianRegisterDate?: string | null;
  isCancellationProRata: boolean;
  isAutoCalculate?: boolean;
};

export type PayDividendJsonData = {
  organizationnumber: string;
  resolutionBy: Resolution;
  resolutionIsBasedOnIntermediateBalanceSheet: boolean;
  repaimentOfPaidInCapital: boolean;
  evenDividendPayment?: PayDividendPayment | null;
  perShareholderDividendPayment: PayDividendRecipients[];
  withholdingTaxPerShareholder?: DividendWithholdingTax[];
  repaymentCalculation?: RepaymentCalculation;
  sharePriceChanges: SharePriceChangeDTO[];
};

export type GroupContributionJsonData = {
  organizationnumber: string;
  companyName: string;
  settlementMethod: GroupMethod;
  amount: Amount;
  resolutionBy: Resolution;
  isResolutionBasedOnIntermediateBalanceSheet: boolean;
  hasBalanceBeenAudited?: boolean;
  decisionDate: string;
  balanceSheetDate?: string | null;
};

export interface SharesToConvertForShareholder {
  shareholderId: number;
  numberOfShares: number;
}

export type ConversionOfSharesDTO = {
  organizationnumber: string;
  fromShareTypeId: number;
  toShareTypeId: number;
  transactionDate: string;
  convertAllShares: boolean;
  sharesToConvertPerShareholder: SharesToConvertForShareholder[];
};

export type ConversionFromISINDTO = {
  organizationnumber: string;
  isinNumber: string;
  toShareTypeId: number;
  transactionDate: string;
};

export type CompanyStatusChangeDTO = {
  organizationnumber: string;
  transactionDate: string;
  status: CompanyStatus;
  previousStatus: CompanyStatus;
  comment?: string;
};

export type RegisterCompanyStatusChangeRequest = {
  organizationnumber: string;
  transactionDate: string;
  status: CompanyStatus;
  comment?: string;
};

export type LiquidationDTO = {
  organizationnumber: string;
  transactionDate: string;
  liquidationSpecifications: {
    shareholderId: number;
    shareTypeId: number;
    totalAmount?: number;
  }[];
  shareTypeSpecifications?: {
    shareTypeId: number;
    averageSharePremiumPerShare?: number;
  }[];
  isAutoCalculate?: boolean;
};

export type MergerDTO = {
  mergerType: MergerType;
  organizationnumber: string;
  transactionDate: string;
  acquiror: NamedOrganisationNumber;
  target: NamedOrganisationNumber;
  mergerCapitalChanges: Array<MergerCapitalChange>;
  targetShareClassData: Array<TargetShareClassData>;
  acquirorNominalValue?: number;
  targetNominalValue?: number;
  shareAllocation: Array<MergerShareAllocation>;
};

export type MergerCapitalChange = {
  targetShareTypeCode: ShareTypeCode;
  acquirorShareTypeId: number;
  paidInCapitalForTarget?: number;
  paidInPremiumForTarget?: number;
  targetNumberOfShares?: number;
  issuedNumberOfShares?: number;
};

export type TargetShareClassData = {
  targetShareTypeId: number;
  acquirorShareTypeCode: ShareTypeCode;
  acquirorNumberOfSharesInShareType: number;
};

export type MergerShareAllocation = {
  shareholderId: number;
  shareTypeId: number;
  numberOfShares: number;
  targetShareTypeCode: ShareTypeCode;
  sharePrice?: number;
  sharePremium?: number;
  paidInShareCapital?: number;
  bonusShareCapital?: number;
};

export type NamedOrganisationNumber = {
  name: string;
  orgNo: string;
};

export enum MergerType {
  ORDINARY_ACQUISITION = "ORDINARY_ACQUISITION",
  PARENT_WHOLLY_OWNED_SUBSIDARY = "PARENT_WHOLLY_OWNED_SUBSIDARY",
  COMPANIES_WITH_SAME_OWNER = "COMPANIES_WITH_SAME_OWNER",
  REVERSE_PARENT_SUBSIDIARY = "REVERSE_PARENT_SUBSIDIARY",
}

export enum DemergerType {
  REDUCTION_OF_NOMINAL_VALUE = "REDUCTION_OF_NOMINAL_VALUE",
  CANCELLATION_OF_SHARES = "CANCELLATION_OF_SHARES",
}

export type ShareholdingAllocation = {
  sharetypeId: number;
  numberOfShares: number;
  shareholderId: number;
  sharenumberIntervals?: SharenumberInterval[];
  entryDate?: string;
  sharePrice?: number;
  sharePremium?: number;
  paidInShareCapital?: number;
  bonusShareCapital?: number;
};

export type ShareholdingPerDateDTO = {
  organizationnumber: string;
  shareAllocation: ShareholdingAllocation[];
  transactionDate: string;
  nominalValue: number;
};

export type ShareClassTotal = {
  sharetypeId: number;
  totalNumberOfShares: number;
};

export type OwnershipRegistrationMethod = "SHARES" | "PERCENT" | "PERCENT_WITH_AUTOMATIC_STRUCTURE";

export type OwnersPerDateDTO = {
  organizationnumber: string;
  transactionDate: string;
  nominalValue: number;
  shareClassTotals: ShareClassTotal[];
  shareAllocation: ShareholdingAllocation[];
  companyStatus?: CompanyStatus;
  registrationMethod?: OwnershipRegistrationMethod;
};

export type DemergerDTO = {
  organizationnumber: string;
  transactionDate: string;
  demergerType: DemergerType;
  acquiror: NamedOrganisationNumber;
  demergerCapitalChanges: Array<DemergerCapitalChange>;
  acquirorNominalValue: number;
  nominalValueReductionAmount: number;
  shareCancellations: Array<{
    shareholderId: number;
    shareTypeId: number;
    numberOfShares: number;
  }>;
};

export type DemergerCapitalChange = {
  transferorShareTypeId: number;
  acquirorShareTypeCode: ShareTypeCode;
  paidInCapitalForTarget: number;
  paidInPremiumForTarget: number;
  considerationNumberOfShares: number;
};

export type ContractNoteRequest = {
  transactionLogId: number;
  shareholderBuyerId: number;
  shareholderSellerId: number;
  fileName: string;
};
